import { useColorModeValue } from "@/components/ui/color-mode"
import { ReactIcon } from "@/components/ui/icon"
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
} from "@/components/ui/menu"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { colors, effects } from "@/theme"
import { Box, Flex, Text } from "@chakra-ui/react"
import { useTranslation } from "react-i18next"
import { FiCheck, FiGlobe } from "react-icons/fi"

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
]

interface LanguageSwitcherProps {
  collapsed?: boolean
}

export const LanguageSwitcher = ({
  collapsed = false,
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation()
  const sc = useSemanticColors()
  const isLight = useColorModeValue(true, false)

  const currentLang =
    languages.find((l) => l.code === i18n.language) || languages[0]

  const menuBg = isLight ? "white" : effects.glass.cardBg
  const menuBorder = isLight ? "#E2E8F0" : colors.glass.whiteAlpha[200]
  const menuShadow = isLight
    ? "0 10px 30px rgba(0, 0, 0, 0.1)"
    : "0 10px 30px rgba(0, 0, 0, 0.3)"
  const itemHoverBg = isLight
    ? "rgba(0, 0, 0, 0.04)"
    : colors.glass.whiteAlpha[100]

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code)
  }

  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Box
          as="button"
          p={2}
          borderRadius="md"
          color={sc.textMuted}
          _hover={{ color: sc.heading, bg: sc.hoverBg }}
          transition="all 0.2s"
          title={currentLang.label}
        >
          {collapsed ? (
            <Text fontSize="sm">{currentLang.flag}</Text>
          ) : (
            <FiGlobe size={18} />
          )}
        </Box>
      </MenuTrigger>

      <MenuContent
        bg={menuBg}
        backdropFilter={isLight ? "none" : "blur(20px)"}
        border="1px solid"
        borderColor={menuBorder}
        borderRadius="12px"
        p={2}
        minW="160px"
        boxShadow={menuShadow}
      >
        {languages.map((lang) => (
          <MenuItem key={lang.code} value={lang.code} asChild>
            <Flex
              as="button"
              w="100%"
              p={3}
              borderRadius="8px"
              _hover={{ bg: itemHoverBg }}
              transition="all 0.2s"
              justify="space-between"
              align="center"
              onClick={() => handleLanguageChange(lang.code)}
            >
              <Flex align="center" gap={2}>
                <Text fontSize="md">{lang.flag}</Text>
                <Text fontSize="sm" color={sc.heading} fontWeight="500">
                  {lang.label}
                </Text>
              </Flex>
              {i18n.language === lang.code && (
                <ReactIcon icon={FiCheck} color="brand.200" fontSize="16px" />
              )}
            </Flex>
          </MenuItem>
        ))}
      </MenuContent>
    </MenuRoot>
  )
}
